import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createQmsCategory,
  type CreateQmsCategoryResult,
} from '../api/createQmsCategoryApiService';
import { QMS_CATEGORY_LABELS } from '../constants/labels';
import { QMS_CATEGORY_ERROR_MESSAGES } from '../errors/errorMessages';
import { useQmsTagSuggestions } from './useQmsTagSuggestions';

export interface CreateQmsCategoryFormData {
  name: string;
  description: string;
  existingTags: string[];
  newTags: string[];
}

export interface CreateQmsCategoryFormErrors {
  name?: string;
  description?: string;
}

const LIST_PATH = '/Kategorie';

const validateName = (value: string): string | undefined => {
  if (!value.trim()) {
    return QMS_CATEGORY_ERROR_MESSAGES.REQUIRED;
  }
  if (value.trim().length > 50) {
    return QMS_CATEGORY_ERROR_MESSAGES.NAME_TOO_LONG;
  }
  return undefined;
};

const validateDescription = (value: string): string | undefined => {
  if (!value.trim()) {
    return QMS_CATEGORY_ERROR_MESSAGES.REQUIRED;
  }
  if (value.trim().length > 500) {
    return QMS_CATEGORY_ERROR_MESSAGES.DESCRIPTION_TOO_LONG;
  }
  return undefined;
};

export const useCreateQmsCategory = () => {
  const navigate = useNavigate();
  const tagSuggestions = useQmsTagSuggestions();

  const [formData, setFormData] = useState<CreateQmsCategoryFormData>({
    name: '',
    description: '',
    existingTags: [],
    newTags: [],
  });
  const [errors, setErrors] = useState<CreateQmsCategoryFormErrors>({});
  const [tagError, setTagError] = useState('');
  const hasPendingTagRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = e.target.value.trimStart().slice(0, 50);
      setFormData((prev) => ({ ...prev, name: next }));
      if (errors.name) {
        setErrors((prev) => {
          const { name: _n, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.name]
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = e.target.value.trimStart().slice(0, 500);
      setFormData((prev) => ({ ...prev, description: next }));
      if (errors.description) {
        setErrors((prev) => {
          const { description: _d, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.description]
  );

  const handleTagsChange = useCallback(
    (existingTags: string[], newTags: string[]) => {
      setFormData((prev) => ({
        ...prev,
        existingTags,
        newTags,
      }));
    },
    []
  );

  const handleTagPendingChange = useCallback((hasPending: boolean) => {
    hasPendingTagRef.current = hasPending;
    if (!hasPending) {
      setTagError('');
    }
  }, []);

  const applyFieldErrors = useCallback(
    (fieldErrors: Record<string, string>): boolean => {
      const mapped: CreateQmsCategoryFormErrors = {};
      if (fieldErrors['name']) {
        mapped.name = fieldErrors['name'];
      }
      if (fieldErrors['description']) {
        mapped.description = fieldErrors['description'];
      }
      if (Object.keys(mapped).length > 0) {
        setErrors(mapped);
        return true;
      }
      return false;
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (hasPendingTagRef.current) {
        setTagError(QMS_CATEGORY_ERROR_MESSAGES.TAG_NOT_ADDED);
        return;
      }

      const nextErrors: CreateQmsCategoryFormErrors = {};
      const nameError = validateName(formData.name);
      if (nameError) {
        nextErrors.name = nameError;
      }
      const descriptionError = validateDescription(formData.description);
      if (descriptionError) {
        nextErrors.description = descriptionError;
      }

      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors);
        return;
      }

      setIsSubmitting(true);
      try {
        const result: CreateQmsCategoryResult = await createQmsCategory({
          name: formData.name.trim(),
          description: formData.description.trim(),
          tags: [...formData.existingTags, ...formData.newTags],
        });

        if (result.success) {
          navigate(LIST_PATH, {
            state: {
              navigationSuccessMessage: QMS_CATEGORY_LABELS.CREATE_SUCCESS,
            },
          });
          return;
        }

        const handledAsFieldError = result.fieldErrors
          ? applyFieldErrors(result.fieldErrors)
          : false;

        if (!handledAsFieldError) {
          setSnackbarError(result.error ?? '');
          setIsSnackbarOpen(true);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, navigate, applyFieldErrors]
  );

  const handleCancel = useCallback(() => {
    const hasContent =
      formData.name.trim() !== '' ||
      formData.description.trim() !== '' ||
      formData.existingTags.length > 0 ||
      formData.newTags.length > 0;
    if (!hasContent) {
      navigate(LIST_PATH);
      return;
    }
    setIsCancelModalOpen(true);
  }, [formData, navigate]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    navigate(LIST_PATH);
  }, [navigate]);

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
  }, []);

  return {
    formData,
    errors,
    tagError,
    isSubmitting,
    isCancelModalOpen,
    isSnackbarOpen,
    snackbarError,
    handleNameChange,
    handleDescriptionChange,
    handleTagsChange,
    handleTagPendingChange,
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
    tagSuggestions: tagSuggestions.suggestions,
    isTagSuggestionsLoading: tagSuggestions.isLoading,
    onTagSearchChange: tagSuggestions.setSearch,
  };
};
